"use client";

import { useState } from "react";

import type { PromptRequirements, PromptTopic } from "@/lib/api/types";
import { formatEuro } from "@/lib/format";
import {
  BRIEFING_SOURCES,
  EVEN_MIX,
  FUNNEL_LABEL,
  FUNNEL_STAGES,
  type FunnelMix,
  briefingFor,
  fanoutText,
  fanoutsStored,
  generateCosts,
  mixHint,
  mixTotal,
  needsBriefing,
} from "@/lib/prompt-module";

export type GenerateRequest = { sources: string[]; briefingId: number | null; seed: string; funnelMix: FunnelMix | null; demand: boolean; refreshFanouts: boolean };

type Props = {
  req: PromptRequirements;
  topics: PromptTopic[];
  topicId: number | null;
  onTopic: (id: number) => void;
  /** Legt ein Topic an und wählt es. Wirft bei einem Fehler. */
  onCreateTopic: (name: string) => Promise<void>;
  onStart: (request: GenerateRequest) => Promise<void>;
  busy: boolean;
  error: string | null;
};

const FIELD = "h-11 rounded-md border border-field bg-white px-3 text-sm";

/** „Prompts generieren“ (Mockup): Topic, Quellen als Chips, Kosten vor dem Start. */
export function GenerateBar({ req, topics, topicId, onTopic, onCreateTopic, onStart, busy, error }: Props) {
  const [selected, setSelected] = useState<string[]>([]);
  const [briefingId, setBriefingId] = useState<number | null>(null);
  const [seed, setSeed] = useState("");
  const [newTopic, setNewTopic] = useState("");
  const [topicError, setTopicError] = useState<string | null>(null);
  const [demand, setDemand] = useState(false);
  const [refreshFanouts, setRefreshFanouts] = useState(false);
  const [mixOn, setMixOn] = useState(false);
  const [mix, setMix] = useState<FunnelMix>(EVEN_MIX);

  const topic = topics.find((t) => t.id === topicId) ?? null;
  const toggle = (key: string) =>
    setSelected((current) => (current.includes(key) ? current.filter((k) => k !== key) : [...current, key]));
  const briefingNeeded = needsBriefing(selected);
  const briefing = req.briefings.find((b) => b.id === briefingId) ?? null;
  const briefingMissing = briefingNeeded && (briefing === null || selected.some((s) => (BRIEFING_SOURCES as readonly string[]).includes(s) && !briefingFor(s, briefing)));
  const mixOk = !mixOn || mixTotal(mix) === 100;
  const ready = topic !== null && selected.length > 0 && !briefingMissing && mixOk && !busy;
  const keywordSources = selected.some((s) => s === "paa" || s === "sistrix");
  const storedFanouts = fanoutsStored(topic);
  const costs = generateCosts(req, selected, topic, refreshFanouts);
  if (demand && selected.length > 0) {
    costs.push(
      `Nachfrage: ca. ${req.demand_units_per_prompt} ahrefs-Units je neuem Prompt, mindestens ${req.demand_units_minimum} (Abo, keine Euro-Kosten), dazu Gemini-Tokens`,
    );
  }

  const create = async () => {
    setTopicError(null);
    try {
      await onCreateTopic(newTopic);
      setNewTopic("");
    } catch (caught) {
      setTopicError(caught instanceof Error ? caught.message : "Unbekannter Fehler");
    }
  };

  return (
    <section aria-labelledby="generate-title" className="flex flex-col gap-4 rounded-lg border border-line bg-white px-5 py-4">
      <h2 id="generate-title" className="text-base font-semibold">
        Prompts generieren
      </h2>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="topic-select" className="text-[13px] font-medium">
            Topic
          </label>
          <select
            id="topic-select"
            value={topicId ?? ""}
            onChange={(event) => onTopic(Number(event.target.value))}
            disabled={topics.length === 0}
            className={`${FIELD} min-w-[220px]`}
          >
            {topics.length === 0 && <option value="">Noch kein Topic</option>}
            {topics.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="topic-new" className="text-[13px] font-medium">
            Neues Topic
          </label>
          <div className="flex gap-2">
            <input
              id="topic-new"
              value={newTopic}
              onChange={(event) => setNewTopic(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && newTopic.trim()) void create();
              }}
              placeholder="z. B. Elektromobilität"
              className={`${FIELD} w-60 max-w-full`}
            />
            <button
              type="button"
              disabled={!newTopic.trim()}
              onClick={() => void create()}
              className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60"
            >
              Anlegen
            </button>
          </div>
        </div>
      </div>
      {topicError && (
        <p role="alert" className="text-[13px] text-danger">
          {topicError}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <span id="sources-label" className="text-[13px] font-medium">
          Generieren aus
        </span>
        <ul aria-labelledby="sources-label" className="flex flex-wrap gap-2">
          {req.sources.map((source) => {
            const on = selected.includes(source.key);
            return (
              <li key={source.key}>
                <button
                  type="button"
                  aria-pressed={on}
                  disabled={!source.available}
                  onClick={() => toggle(source.key)}
                  className={`flex min-h-11 flex-col items-start justify-center rounded-md border px-3.5 py-1.5 text-left text-[13px] disabled:opacity-60 ${
                    on ? "border-ink bg-ink text-white" : "border-field bg-white"
                  }`}
                >
                  <span className="font-medium">{source.label}</span>
                  <span className={`text-xs ${on ? "text-[#d5dae0]" : "text-muted"}`}>{!source.available ? source.reason : source.key === "fanout_live" && fanoutsStored(topic) ? fanoutText(topic) : source.cost}</span>
                </button>
              </li>
            );
          })}
          {req.disabled.map((source) => (
            <li key={source.key}>
              <button
                type="button"
                disabled
                className="flex min-h-11 flex-col items-start justify-center rounded-md border border-neutral-bg bg-neutral-bg px-3.5 py-1.5 text-left text-[13px] opacity-80"
              >
                <span className="font-medium">{source.label}</span>
                <span className="text-xs text-muted">{source.reason}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {briefingNeeded && (
        <div className="flex flex-col gap-1">
          <label htmlFor="briefing-select" className="text-[13px] font-medium">
            Briefing für Briefing-Prompts, Fan-outs und Cluster
          </label>
          <select
            id="briefing-select"
            value={briefingId ?? ""}
            onChange={(event) => setBriefingId(event.target.value ? Number(event.target.value) : null)}
            className={`${FIELD} max-w-md`}
          >
            <option value="">Briefing wählen …</option>
            {req.briefings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.keyword}
              </option>
            ))}
          </select>
          {briefingMissing && briefing !== null && (
            <p className="text-xs text-warning">Dieses Briefing hat nicht für alle gewählten Quellen ein Ergebnis (Schritt 2, 3 oder 5).</p>
          )}
        </div>
      )}

      {keywordSources && (
        <div className="flex flex-col gap-1">
          <label htmlFor="seed" className="text-[13px] font-medium">
            Keyword für People Also Ask und SISTRIX
          </label>
          <input
            id="seed"
            value={seed}
            onChange={(event) => setSeed(event.target.value)}
            placeholder={topic?.name ?? "Standard: Name des Topics"}
            className={`${FIELD} max-w-md`}
          />
        </div>
      )}

      {selected.includes("fanout_live") && storedFanouts && (
        <label className="flex min-h-11 items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={refreshFanouts}
            onChange={(event) => setRefreshFanouts(event.target.checked)}
            className="size-5 accent-accent"
          />
          Fan-outs neu abrufen (kostet ca. {formatEuro(req.fanout_eur)}, sonst werden die gespeicherten genutzt)
        </label>
      )}

      <label className="flex min-h-11 items-center gap-2 text-[13px]">
        <input
          type="checkbox"
          checked={demand && req.demand_available}
          disabled={!req.demand_available}
          onChange={(event) => setDemand(event.target.checked)}
          className="size-5 accent-accent"
        />
        <span>
          Nachfrage ermitteln (Suchvolumen über das Parent Topic, ahrefs)
          {!req.demand_available && req.demand_reason ? <span className="text-muted"> · {req.demand_reason}</span> : null}
        </span>
      </label>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-[13px] font-medium">Anteile der Funnel-Stufen</legend>
        <label className="flex min-h-11 items-center gap-2 text-[13px]">
          <input type="checkbox" checked={mixOn} onChange={(event) => setMixOn(event.target.checked)} className="size-5 accent-accent" />
          Anteile festlegen (sonst ohne Vorgabe)
        </label>
        {mixOn && (
          <>
            <div className="flex flex-wrap items-end gap-3">
              {FUNNEL_STAGES.map((stage) => (
                <div key={stage} className="flex flex-col gap-1">
                  <label htmlFor={`mix-${stage}`} className="text-[13px]">
                    {FUNNEL_LABEL[stage]} in %
                  </label>
                  <input
                    id={`mix-${stage}`}
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={100}
                    step={1}
                    value={mix[stage]}
                    onChange={(event) =>
                      setMix((current) => ({ ...current, [stage]: Math.min(100, Math.max(0, Math.round(Number(event.target.value) || 0))) }))
                    }
                    className={`${FIELD} w-28`}
                  />
                </div>
              ))}
              <button
                type="button"
                onClick={() => setMix(EVEN_MIX)}
                className="h-11 rounded-md border border-field bg-white px-3 text-[13px]"
              >
                Gleichmäßig
              </button>
            </div>
            <p role="status" className={`text-[13px] ${mixOk ? "text-muted" : "font-medium text-warning"}`}>
              {mixHint(mix)}
            </p>
            <p className="max-w-3xl text-xs text-muted">
              Gemini erzeugt Personas- und Aspekt-Prompts nach diesen Anteilen. Bei den anderen Quellen steht die Phase erst nach der
              Validierung fest: Dann bleiben je Stufe die Prompts mit dem besten Score, sodass das Verhältnis stimmt. 0 % schließt eine
              Stufe aus. Aussortierte Prompts stehen unter „Verworfen“ und lassen sich zurückholen.
            </p>
          </>
        )}
      </fieldset>

      <div className="flex flex-col gap-1">
        <span className="text-[13px] font-medium">Kosten vor dem Start</span>
        {costs.length === 0 ? (
          <span className="text-[13px] text-muted">Wähle mindestens eine Quelle.</span>
        ) : (
          <ul className="flex flex-col gap-0.5 text-[13px] text-[#3a424a]">
            {costs.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
      </div>

      {error && (
        <p role="alert" className="text-[13px] text-danger">
          {error}
        </p>
      )}
      <div>
        <button
          type="button"
          disabled={!ready}
          onClick={() =>
            void onStart({ sources: selected, briefingId: briefingNeeded ? briefingId : null, seed: seed.trim(), funnelMix: mixOn ? mix : null, demand: demand && req.demand_available, refreshFanouts: refreshFanouts && storedFanouts })
          }
          className="h-11 rounded-md bg-accent px-4 text-sm font-medium text-white disabled:opacity-60"
        >
          {busy ? "Wird gestartet …" : "Generieren"}
        </button>
      </div>
    </section>
  );
}
